import React, { useState, useEffect } from 'react';
import { salesAPI } from '../services/api';
import Toast from './Toast';

/**
 * CobranzaModal — Modal reutilizable para registrar cobros sobre ventas pendientes.
 * Props:
 *   customer  {object}   — Cliente con { id, name, total_debt }
 *   onClose   {function} — Callback para cerrar el modal
 *   onPaid    {function} — Callback tras pago exitoso (para refrescar la lista padre)
 */
const CobranzaModal = ({ customer, onClose, onPaid }) => {
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(false);
    const [payingId, setPayingId] = useState(null);
    const [payAmounts, setPayAmounts] = useState({});
    const [toast, setToast] = useState(null);

    const showToast = (message, type = 'success') => setToast({ message, type });

    // Cargar ventas pendientes al montar o al cambiar el cliente
    useEffect(() => {
        if (!customer) return;
        loadPendingSales();
    }, [customer?.id]);

    const loadPendingSales = async () => {
        setLoading(true);
        try {
            const response = await salesAPI.getAll({ customer_id: customer.id, limit: 100 });
            const allSales = response.data.data?.sales || response.data.sales || [];
            const pending = allSales.filter(s =>
                s.payment_status === 'pendiente' || s.payment_status === 'parcial'
            );
            setSales(pending);
            // Pre-cargar cada input con el saldo total de la venta
            const initAmounts = {};
            pending.forEach(s => { initAmounts[s.id] = parseFloat(s.balance_due || 0).toFixed(2); });
            setPayAmounts(initAmounts);
        } catch {
            showToast('Error al cargar ventas pendientes', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePagar = async (sale) => {
        const amount = parseFloat(payAmounts[sale.id]);
        const balance = parseFloat(sale.balance_due || 0);

        if (!amount || amount <= 0) {
            showToast('Ingresa un monto válido mayor a 0', 'error');
            return;
        }
        if (amount > balance + 0.01) {
            showToast(`El monto no puede superar el saldo de S/. ${balance.toFixed(2)}`, 'error');
            return;
        }

        setPayingId(sale.id);
        try {
            await salesAPI.addPayment(sale.id, {
                amount,
                payment_method: 'efectivo',
                notes: 'Cobro registrado desde panel de clientes'
            });
            showToast(`✅ Pago de S/. ${amount.toFixed(2)} registrado`, 'success');
            await loadPendingSales();   // Refrescar lista sin cerrar el modal
            if (onPaid) onPaid();       // Notificar al padre para que refresque su tabla
        } catch (error) {
            showToast(error.response?.data?.message || 'Error al registrar el pago', 'error');
        } finally {
            setPayingId(null);
        }
    };

    if (!customer) return null;

    const totalDebt = parseFloat(customer.total_debt || 0);

    return (
        <>
            <div className="modal-overlay" onClick={onClose}>
                <div
                    className="modal-wide"
                    onClick={e => e.stopPropagation()}
                    style={{ maxWidth: 700, width: '95%' }}
                >
                    {/* ── Header ── */}
                    <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '1.2rem' }}>
                                💳 Cobrar a: <strong>{customer.name}</strong>
                            </h2>
                            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                                Deuda total:&nbsp;
                                <strong style={{ color: '#dc2626' }}>
                                    S/. {totalDebt.toFixed(2)}
                                </strong>
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b' }}
                        >
                            ✕
                        </button>
                    </div>

                    {/* ── Body ── */}
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                            ⏳ Cargando ventas pendientes...
                        </div>
                    ) : sales.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#16a34a', fontSize: '1rem' }}>
                            ✅ Este cliente no tiene deudas pendientes
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <thead>
                                    <tr style={{ background: 'var(--bg-table-header)', borderBottom: '2px solid var(--border-color)' }}>
                                        <th style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 600 }}>Fecha</th>
                                        <th style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 600 }}>N° Venta</th>
                                        <th style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text-secondary)', fontWeight: 600 }}>Total</th>
                                        <th style={{ padding: '10px 12px', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>Saldo</th>
                                        <th style={{ padding: '10px 12px', textAlign: 'center', color: 'var(--text-secondary)', fontWeight: 600 }}>Estado</th>
                                        <th style={{ padding: '10px 12px', textAlign: 'center', color: 'var(--text-secondary)', fontWeight: 600 }}>Registrar Pago</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sales.map(sale => (
                                        <tr key={sale.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                            <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>
                                                {sale.created_at ? new Date(sale.created_at).toLocaleDateString('es-PE') : '-'}
                                            </td>
                                            <td style={{ padding: '10px 12px', fontFamily: 'monospace', color: '#6366f1', fontWeight: 600 }}>
                                                {sale.correlativo_local ? `VTA-${String(sale.correlativo_local).padStart(5, '0')}` : `#${sale.id}`}
                                            </td>
                                            <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                                S/. {parseFloat(sale.total_amount || 0).toFixed(2)}
                                            </td>
                                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>
                                                S/. {parseFloat(sale.balance_due || 0).toFixed(2)}
                                            </td>
                                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                                <span style={{
                                                    background: sale.payment_status === 'parcial' ? '#fef3c7' : '#fef2f2',
                                                    color: sale.payment_status === 'parcial' ? '#d97706' : '#dc2626',
                                                    padding: '3px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600
                                                }}>
                                                    {sale.payment_status === 'parcial' ? '⚡ Parcial' : '⏳ Pendiente'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '10px 12px' }}>
                                                <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center' }}>
                                                    <span style={{ color: '#64748b', fontSize: '0.85rem', flexShrink: 0 }}>S/.</span>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0.01"
                                                        max={parseFloat(sale.balance_due || 0)}
                                                        value={payAmounts[sale.id] || ''}
                                                        onChange={e => setPayAmounts(prev => ({ ...prev, [sale.id]: e.target.value }))}
                                                        style={{
                                                            width: 75, padding: '4px 6px',
                                                            border: '1px solid #cbd5e1', borderRadius: 6,
                                                            fontSize: '0.88rem', textAlign: 'right'
                                                        }}
                                                    />
                                                    <button
                                                        onClick={() => handlePagar(sale)}
                                                        disabled={payingId === sale.id}
                                                        style={{
                                                            background: payingId === sale.id ? '#94a3b8' : '#16a34a',
                                                            color: '#fff', border: 'none', borderRadius: 6,
                                                            padding: '5px 12px',
                                                            cursor: payingId === sale.id ? 'not-allowed' : 'pointer',
                                                            fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap',
                                                            transition: 'background 0.2s'
                                                        }}
                                                    >
                                                        {payingId === sale.id ? '⏳' : '✓ Pagar'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
                        <button onClick={onClose} className="btn btn-secondary">Cerrar</button>
                    </div>
                </div>
            </div>

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </>
    );
};

export default CobranzaModal;
